import {
    ContentScriptOutboundMessage,
    ControllerOutboundMessage,
    HandshakeMessage,
    CONTROLLER_PROTOCOL_VERSION
} from "../../shared/ChannelProtocol";

type InboundHandler = (msg: ContentScriptOutboundMessage) => void;

/**
 * Single connection to the content script that hosts this iframe.
 *
 * The content script sends a {kind: "handshake"} message once the iframe has
 * loaded; after that, both sides use structured `ContentScript*` /
 * `Controller*` messages. The channel only accepts messages whose
 * `event.source === window.parent` and whose `event.origin` matches the page
 * URL we were loaded into.
 */
export class ContentScriptChannel {
    private tabId: string | null = null;
    private parentOrigin: string | null = null;
    private handler: InboundHandler | null = null;
    private readyResolvers: Array<() => void> = [];

    constructor() {
        window.addEventListener("message", this.handleWindowMessage);
    }

    onMessage(handler: InboundHandler): void {
        this.handler = handler;
    }

    async waitUntilReady(): Promise<void> {
        if (this.tabId) return;
        await new Promise<void>((resolve) => this.readyResolvers.push(resolve));
    }

    getTabId(): string {
        if (!this.tabId) throw new Error("ContentScriptChannel not ready");
        return this.tabId;
    }

    send(message: ControllerOutboundMessage): void {
        if (!this.parentOrigin || !this.tabId) {
            console.warn("[controller-channel] drop message before handshake", message);
            return;
        }
        window.parent.postMessage(message, this.parentOrigin);
    }

    dispose(): void {
        window.removeEventListener("message", this.handleWindowMessage);
        this.handler = null;
        this.tabId = null;
        this.parentOrigin = null;
    }

    private handleWindowMessage = (event: MessageEvent): void => {
        if (event.source !== window.parent) return;

        const data = event.data;
        if (!data || typeof data !== "object" || typeof (data as any).kind !== "string") return;

        if ((data as HandshakeMessage).kind === "handshake") {
            const handshake = data as HandshakeMessage;
            if (handshake.protocolVersion !== CONTROLLER_PROTOCOL_VERSION) {
                console.warn("[controller-channel] unsupported handshake version", handshake.protocolVersion);
                return;
            }
            this.tabId = handshake.tabId;
            this.parentOrigin = event.origin;
            this.readyResolvers.splice(0).forEach((r) => r());
            return;
        }

        if (this.parentOrigin && event.origin !== this.parentOrigin) return;
        if (!this.handler) return;

        const message = data as ContentScriptOutboundMessage;
        if (typeof message.kind !== "string") return;
        this.handler(message);
    };
}
